import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Loader2, ArrowLeft, ArrowRight, Truck, HandHelping, Building2, Globe, Star,
  CheckCircle, Shield, Search, Lock, Layers, Check, ChevronRight, ChevronLeft,
} from 'lucide-react';
import { toast } from 'sonner';

export const MeasureFormDialog = ({
  open, onOpenChange,
  editingMeasure, wizardStep, setWizardStep,
  formData, setFormData,
  actionLoading, handleSave, suppliers,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-xl">
      <DialogHeader className="pb-3 border-b border-neutral-100">
        <DialogTitle className="text-base font-semibold">
          {editingMeasure ? 'Maatregel bewerken' : 'Nieuwe maatregel'}
        </DialogTitle>
        <DialogDescription className="text-sm text-neutral-500">
          {wizardStep === 1 && 'Stap 1 van 4: Basisgegevens'}
          {wizardStep === 2 && 'Stap 2 van 4: Domein & Impact'}
          {wizardStep === 3 && 'Stap 3 van 4: Kosten & Leverancier'}
          {wizardStep === 4 && 'Stap 4 van 4: Bevestiging'}
        </DialogDescription>
      </DialogHeader>

      <div className="flex items-center gap-1.5 px-1" data-testid="wizard-steps">
        {[1,2,3,4].map(s => (
          <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= wizardStep ? 'bg-black' : 'bg-neutral-200'}`} />
        ))}
      </div>

      {wizardStep === 1 && (
        <div className="space-y-5">
          {formData._smartDefault && (
            <div className="p-3 rounded-xl bg-neutral-50 border border-neutral-200" data-testid="smart-default-banner">
              <p className="text-xs font-medium text-black">
                Voorgevuld op basis van: <span className="text-neutral-600">{formData._linkedRiskTitle}</span>
              </p>
              <p className="text-[10px] text-neutral-500 mt-0.5">Je kunt alle velden aanpassen</p>
            </div>
          )}
          <div className="space-y-3">
            <Label className="text-xs font-medium text-neutral-600">Naam *</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="MFA implementeren voor alle gebruikers" data-testid="measure-name-input" />
          </div>
          <div className="space-y-3">
            <Label className="text-xs font-medium text-neutral-600">Beschrijving</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Gedetailleerde beschrijving van de maatregel..." rows={3} />
          </div>
          <div className="space-y-3">
            <Label className="text-xs font-medium text-neutral-600">Doel</Label>
            <Input value={formData.goal} onChange={(e) => setFormData({ ...formData, goal: e.target.value })} placeholder="Verhogen van toegangsbeveiliging" />
          </div>
        </div>
      )}

      {wizardStep === 2 && (
        <div className="space-y-4">
          <div>
            <Label>Domein</Label>
            <Select value={formData.domain} onValueChange={(v) => setFormData({ ...formData, domain: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="people">Mens</SelectItem>
                <SelectItem value="process">Proces</SelectItem>
                <SelectItem value="technology">Technologie</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Complexiteit</Label>
            <Select value={formData.complexity} onValueChange={(v) => setFormData({ ...formData, complexity: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Eenvoudig</SelectItem>
                <SelectItem value="medium">Gemiddeld</SelectItem>
                <SelectItem value="high">Complex</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Impact (1-5)</Label>
            <Select value={String(formData.impact)} onValueChange={(v) => setFormData({ ...formData, impact: parseInt(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((i) => (
                  <SelectItem key={i} value={String(i)}>{i}: {i <= 2 ? 'Laag' : i <= 3 ? 'Gemiddeld' : 'Hoog'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {wizardStep === 3 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label>Status</Label>
              <Select value={formData.status || 'planned'} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Gepland</SelectItem>
                  <SelectItem value="in_progress">In uitvoering</SelectItem>
                  <SelectItem value="implemented">Voltooid</SelectItem>
                  <SelectItem value="verified">Geverifieerd</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kostenindicatie (€)</Label>
              <Input type="number" value={formData.cost_indication} onChange={(e) => setFormData({ ...formData, cost_indication: e.target.value })} placeholder="5000" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label>Bron / Framework</Label>
              <Select value={formData.framework || 'none'} onValueChange={(v) => setFormData({ ...formData, framework: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Selecteer bron..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Eigen maatregel</SelectItem>
                  <SelectItem value="ISO 27001">ISO 27001</SelectItem>
                  <SelectItem value="NIS2">NIS2</SelectItem>
                  <SelectItem value="AVG/GDPR">AVG/GDPR</SelectItem>
                  <SelectItem value="BIO">BIO</SelectItem>
                  <SelectItem value="CIS Controls">CIS Controls</SelectItem>
                  <SelectItem value="NIST CSF">NIST CSF</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Control referentie</Label>
              <Input value={formData.control_ref || ''} onChange={(e) => setFormData({ ...formData, control_ref: e.target.value })} placeholder="Bijv. A.8.1 of PR.AC-1" />
            </div>
          </div>
          <div>
            <Label className="flex items-center gap-2">
              <Truck className="h-4 w-4" strokeWidth={1.5} />
              Leverancier koppelen
            </Label>
            <Select value={formData.supplier_id || "none"} onValueChange={(v) => setFormData({ ...formData, supplier_id: v === "none" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="Selecteer leverancier..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Geen leverancier</SelectItem>
                {suppliers.filter(s => s.is_verified).map((supplier) => (
                  <SelectItem key={supplier.id} value={supplier.id}>
                    {supplier.company_name} {supplier.rating > 0 && `(★${supplier.rating.toFixed(1)})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-neutral-500 mt-1">Koppel een geverifieerde leverancier die deze maatregel kan implementeren</p>
          </div>
        </div>
      )}

      {wizardStep === 4 && (
        <div className="space-y-3" data-testid="wizard-confirm">
          <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200 space-y-3">
            <div className="flex justify-between text-sm"><span className="text-neutral-500">Naam</span><span className="font-medium text-black">{formData.name || '-'}</span></div>
            {formData.goal && <div className="flex justify-between text-sm"><span className="text-neutral-500">Doel</span><span className="text-black">{formData.goal}</span></div>}
            <div className="h-px bg-neutral-200" />
            <div className="flex justify-between text-sm"><span className="text-neutral-500">Domein</span><span className="text-black">{formData.domain === 'people' ? 'Mens' : formData.domain === 'process' ? 'Proces' : 'Technologie'}</span></div>
            <div className="flex justify-between text-sm"><span className="text-neutral-500">Complexiteit</span><span className="text-black">{formData.complexity === 'low' ? 'Eenvoudig' : formData.complexity === 'medium' ? 'Gemiddeld' : 'Complex'}</span></div>
            <div className="flex justify-between text-sm"><span className="text-neutral-500">Impact</span><span className="text-black">{formData.impact}/5</span></div>
            {formData.cost_indication && (<><div className="h-px bg-neutral-200" /><div className="flex justify-between text-sm"><span className="text-neutral-500">Kosten</span><span className="font-medium text-black">€{Number(formData.cost_indication).toLocaleString()}</span></div></>)}
          </div>
        </div>
      )}

      <DialogFooter className="gap-2">
        {wizardStep > 1 && (
          <Button variant="outline" onClick={() => setWizardStep(wizardStep - 1)} data-testid="wizard-back">
            <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Terug
          </Button>
        )}
        <div className="flex-1" />
        {wizardStep < 4 ? (
          <Button className="bg-black text-white hover:bg-neutral-800" onClick={() => { if (wizardStep === 1 && !formData.name.trim()) { toast.error('Naam is verplicht'); return; } setWizardStep(wizardStep + 1); }} data-testid="wizard-next">
            Volgende <ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
          </Button>
        ) : (
          <Button onClick={handleSave} disabled={actionLoading} className="bg-black text-white hover:bg-neutral-800" data-testid="save-measure-btn">
            {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editingMeasure ? 'Opslaan' : 'Aanmaken'}
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const HelpWizardDialog = ({
  open, onOpenChange,
  helpWizardStep, helpRequestData, setHelpRequestData,
  measures, matchedSuppliers, selectedSupplierForHelp, setSelectedSupplierForHelp,
  actionLoading, getStatusBadge, toggleMeasureSelection,
  handleHelpWizardNext, handleHelpWizardBack,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <HandHelping className="h-5 w-5 text-blue-600" /> Hulp inschakelen
        </DialogTitle>
        <DialogDescription>
          {helpWizardStep === 1 && "Stap 1 van 3: Beschrijf uw hulpvraag"}
          {helpWizardStep === 2 && "Stap 2 van 3: Contactgegevens & datadeling"}
          {helpWizardStep === 3 && "Stap 3 van 3: Kies een leverancier"}
        </DialogDescription>
      </DialogHeader>

      <div className="flex items-center gap-2 mt-2">
        {[1, 2, 3].map((step) => (
          <div key={step} className={`flex-1 h-1.5 rounded-full ${step <= helpWizardStep ? 'bg-blue-600' : 'bg-neutral-200'}`} />
        ))}
      </div>

      <div className="py-4">
        {helpWizardStep === 1 && (
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Maatregelen voor hulpvraag</Label>
              <div className="border border-neutral-200 rounded-lg max-h-48 overflow-y-auto">
                {measures.filter(m => m.status !== 'verified').map(m => (
                  <label key={m.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-neutral-50 cursor-pointer border-b border-neutral-100 last:border-0" data-testid={`help-measure-checkbox-${m.id}`}>
                    <Checkbox checked={(helpRequestData.selected_measure_ids || []).includes(m.id)} onCheckedChange={() => toggleMeasureSelection(m.id)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-black truncate">{m.name}</p>
                      <p className="text-[10px] text-neutral-500">{m.measure_id} · {m.domain === 'people' ? 'Mens' : m.domain === 'process' ? 'Proces' : 'Technologie'}</p>
                    </div>
                    {getStatusBadge(m.status)}
                  </label>
                ))}
                {measures.filter(m => m.status !== 'verified').length === 0 && (
                  <p className="p-4 text-sm text-neutral-500 text-center">Geen beschikbare maatregelen</p>
                )}
              </div>
              <p className="text-[10px] text-neutral-500 mt-1">{(helpRequestData.selected_measure_ids || []).length} maatregel(en) geselecteerd</p>
            </div>
            <div>
              <Label>Type hulp</Label>
              <Select value={helpRequestData.help_type} onValueChange={(v) => setHelpRequestData({ ...helpRequestData, help_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="implementatiehulp">Implementatiehulp</SelectItem>
                  <SelectItem value="advies">Advies / Consultatie</SelectItem>
                  <SelectItem value="toolingselectie">Toolingselectie</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Beschrijf uw hulpvraag *</Label>
              <Textarea value={helpRequestData.summary} onChange={(e) => setHelpRequestData({ ...helpRequestData, summary: e.target.value })} placeholder="Waar heeft u precies hulp bij nodig?" rows={3} />
            </div>
            <div>
              <Label>Gewenst resultaat</Label>
              <Input value={helpRequestData.desired_outcome} onChange={(e) => setHelpRequestData({ ...helpRequestData, desired_outcome: e.target.value })} placeholder="Wat wilt u bereiken?" />
            </div>
            <div>
              <Label>Urgentie</Label>
              <Select value={helpRequestData.urgency} onValueChange={(v) => setHelpRequestData({ ...helpRequestData, urgency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="laag">Laag - Binnen 3 maanden</SelectItem>
                  <SelectItem value="normaal">Normaal - Binnen 1 maand</SelectItem>
                  <SelectItem value="hoog">Hoog - Zo snel mogelijk</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {helpWizardStep === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <div><Label>Uw naam *</Label><Input value={helpRequestData.contact_name} onChange={(e) => setHelpRequestData({ ...helpRequestData, contact_name: e.target.value })} placeholder="Uw volledige naam" /></div>
              <div><Label>E-mailadres *</Label><Input type="email" value={helpRequestData.contact_email} onChange={(e) => setHelpRequestData({ ...helpRequestData, contact_email: e.target.value })} placeholder="email@bedrijf.nl" /></div>
            </div>
            <div><Label>Telefoonnummer (optioneel)</Label><Input type="tel" value={helpRequestData.contact_phone} onChange={(e) => setHelpRequestData({ ...helpRequestData, contact_phone: e.target.value })} placeholder="+31 6 12345678" /></div>
            <div className="border-t pt-4 mt-4">
              <Label className="mb-3 block">Extra informatie delen (optioneel)</Label>
              <p className="text-sm text-neutral-500 mb-3">Deel extra context met leveranciers om een betere match te krijgen.</p>
              <div className="space-y-3">
                {[
                  { id: 'share_company_size', label: 'Bedrijfsgrootte delen' },
                  { id: 'share_sector', label: 'Sector delen' },
                  { id: 'share_region', label: 'Regio delen' },
                ].map(opt => (
                  <div key={opt.id} className="flex items-center gap-3">
                    <Checkbox id={opt.id} checked={helpRequestData[opt.id]} onCheckedChange={(c) => setHelpRequestData({ ...helpRequestData, [opt.id]: c })} />
                    <label htmlFor={opt.id} className="text-sm cursor-pointer">{opt.label}</label>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 mt-4">
              <p className="text-sm text-blue-700"><strong>Privacy:</strong> Uw assessment data, scores en exports worden <strong>nooit</strong> automatisch gedeeld. Alleen de informatie die u hierboven selecteert wordt met de leverancier gedeeld.</p>
            </div>
          </div>
        )}

        {helpWizardStep === 3 && (
          <div className="space-y-4">
            {matchedSuppliers.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <Truck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Geen leveranciers gevonden die aan uw criteria voldoen.</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-neutral-600 mb-4">Selecteer een leverancier om uw aanvraag naar te sturen. Spooker rangschikt niet - u kiest zelf.</p>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {matchedSuppliers.map((supplier) => (
                    <div key={supplier.id} onClick={() => setSelectedSupplierForHelp(supplier)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${selectedSupplierForHelp?.id === supplier.id ? 'border-blue-500 bg-blue-50' : 'border-neutral-200 hover:border-neutral-300'}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-neutral-400" />
                            <span className="font-medium text-neutral-900">{supplier.company_name}</span>
                            {supplier.rating > 0 && <Badge className="bg-yellow-50 text-yellow-700 border-transparent"><Star className="h-3 w-3 mr-1 fill-yellow-500" />{supplier.rating.toFixed(1)}</Badge>}
                          </div>
                          <p className="text-sm text-neutral-600 mt-1 line-clamp-2">{supplier.description}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {supplier.services?.slice(0, 4).map((service) => (<Badge key={service} variant="secondary" className="text-xs">{service}</Badge>))}
                          </div>
                          {supplier.website && <div className="flex items-center gap-1 mt-2 text-xs text-neutral-500"><Globe className="h-3 w-3" />{supplier.website}</div>}
                        </div>
                        {selectedSupplierForHelp?.id === supplier.id && <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <DialogFooter className="flex justify-between">
        <div>
          {helpWizardStep > 1 && (
            <Button variant="outline" onClick={handleHelpWizardBack} disabled={actionLoading}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Vorige
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button onClick={handleHelpWizardNext} disabled={actionLoading || (helpWizardStep === 3 && matchedSuppliers.length === 0)} className="bg-blue-600 text-white hover:bg-blue-700">
            {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {helpWizardStep === 3 ? 'Versturen' : 'Volgende'}
            {helpWizardStep < 3 && <ArrowRight className="h-4 w-4 ml-2" />}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const FrameworkWizardDialog = ({
  open, onOpenChange,
  fwStep, setFwStep,
  selectedFramework, frameworkControls, selectedControls, setSelectedControls,
  controlSearch, setControlSearch, fwCreating,
  getAvailableFrameworks, getAllFrameworks, handleSelectFramework,
  toggleControl, handleCreateFromControls, measures,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
      <DialogHeader>
        <DialogTitle>Maatregelen uit Framework</DialogTitle>
        <DialogDescription>
          {fwStep === 0 && 'Stap 1 van 3: kies een framework'}
          {fwStep === 1 && `Stap 2 van 3: selecteer controls uit ${selectedFramework?.name || ''}`}
          {fwStep === 2 && `Stap 3 van 3: bevestig ${selectedControls.length} maatregel(en)`}
        </DialogDescription>
      </DialogHeader>

      <div className="flex items-center gap-1 px-1" data-testid="fw-wizard-progress">
        {['Framework', 'Beheersmaatregelen', 'Bevestig'].map((label, i) => (
          <div key={label} className="flex items-center flex-1">
            <div className={`flex items-center gap-1.5 flex-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
              i < fwStep ? 'text-green-700 bg-green-50' : i === fwStep ? 'text-black bg-neutral-100' : 'text-neutral-400'}`}>
              <div className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold ${
                i < fwStep ? 'bg-green-600 text-white' : i === fwStep ? 'bg-black text-white' : 'bg-neutral-200 text-neutral-500'}`}>
                {i < fwStep ? <Check className="h-3 w-3" strokeWidth={2} /> : i + 1}
              </div>
              <span className="hidden sm:inline">{label}</span>
            </div>
            {i < 2 && <div className={`w-4 h-px shrink-0 ${i < fwStep ? 'bg-green-300' : 'bg-neutral-200'}`} />}
          </div>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        {fwStep === 0 && (
          <div className="space-y-3" data-testid="fw-step-framework">
            {getAvailableFrameworks().length > 0 ? (
              <>
                <p className="text-xs text-neutral-500">Selecteer het framework waaruit u controls wilt overnemen als maatregelen:</p>
                <div className="space-y-3">
                  {getAvailableFrameworks().map(fw => (
                    <button key={fw.id} onClick={() => handleSelectFramework(fw)}
                      className="w-full flex items-center gap-4 p-4 rounded-lg border border-neutral-200 text-left hover:border-black hover:shadow-sm transition-all"
                      data-testid={`fw-select-${fw.id}`}>
                      <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><Shield className="h-5 w-5 text-blue-600" strokeWidth={1.5} /></div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-black">{fw.name} <span className="text-neutral-400 font-normal text-xs">v{fw.version}</span></h4>
                        <p className="text-xs text-neutral-500">{fw.description}</p>
                      </div>
                      <div className="text-right shrink-0"><Badge variant="outline" className="text-xs">{fw.controls?.length || fw.control_count || 0} controls</Badge></div>
                      <ChevronRight className="h-4 w-4 text-neutral-400 shrink-0" />
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" strokeWidth={1.5} />
                <h4 className="text-sm font-medium text-black mb-2">Geen actieve framework-modules</h4>
                <p className="text-xs text-neutral-500 mb-4">Om maatregelen uit een framework te kiezen, moet u eerst een compliance module activeren (bijv. ISO 27001 of NIS2).</p>
                <Button variant="outline" size="sm" onClick={() => { onOpenChange(false); window.location.href = '/modules'; }} data-testid="fw-go-to-modules">
                  <Layers className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Ga naar Modules
                </Button>
              </div>
            )}
            {getAvailableFrameworks().length === 0 && getAllFrameworks().length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-neutral-400 mb-2">Beschikbare frameworks (module-activatie vereist):</p>
                <div className="space-y-3 opacity-50">
                  {getAllFrameworks().map(fw => (
                    <div key={fw.id} className="flex items-center gap-3 p-3 rounded-lg border border-neutral-200 border-dashed">
                      <Shield className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                      <span className="text-xs text-neutral-500">{fw.name}, {fw.controls?.length || fw.control_count || 0} controls</span>
                      <Lock className="h-3 w-3 text-neutral-300 ml-auto" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {fwStep === 1 && (
          <div className="space-y-3" data-testid="fw-step-controls">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input type="text" value={controlSearch} onChange={e => setControlSearch(e.target.value)} placeholder="Zoek controls..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-black" data-testid="fw-control-search" />
              </div>
              <Button variant="outline" size="sm" onClick={() => { selectedControls.length === frameworkControls.length ? setSelectedControls([]) : setSelectedControls(frameworkControls.map(c => c.id)); }}
                className="text-xs shrink-0" data-testid="fw-toggle-all">
                {selectedControls.length === frameworkControls.length ? 'Deselecteer alles' : 'Selecteer alles'}
              </Button>
            </div>
            <p className="text-[10px] text-neutral-400">{selectedControls.length} van {frameworkControls.length} geselecteerd</p>
            <div className="space-y-2.5 max-h-[340px] overflow-y-auto">
              {frameworkControls
                .filter(c => !controlSearch || c.name.toLowerCase().includes(controlSearch.toLowerCase()) || c.code.toLowerCase().includes(controlSearch.toLowerCase()))
                .map(ctrl => {
                  const selected = selectedControls.includes(ctrl.id);
                  const alreadyExists = measures.some(m => m.control_id === ctrl.id && m.framework_id === selectedFramework.id);
                  return (
                    <button key={ctrl.id} onClick={() => !alreadyExists && toggleControl(ctrl.id)} disabled={alreadyExists}
                      className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                        alreadyExists ? 'border-green-200 bg-green-50/50 cursor-not-allowed' :
                        selected ? 'border-black bg-neutral-50 ring-1 ring-black' : 'border-neutral-200 hover:border-neutral-300'}`}
                      data-testid={`fw-control-${ctrl.id}`}>
                      <div className={`h-5 w-5 rounded border flex items-center justify-center shrink-0 mt-0.5 ${
                        alreadyExists ? 'bg-green-100 border-green-300' : selected ? 'bg-black border-black' : 'border-neutral-300'}`}>
                        {(selected || alreadyExists) && <Check className={`h-3 w-3 ${alreadyExists ? 'text-green-600' : 'text-white'}`} strokeWidth={2} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-neutral-500">{ctrl.code}</span>
                          {ctrl.category && <Badge variant="outline" className="text-[10px]">{ctrl.category}</Badge>}
                          {alreadyExists && <Badge className="bg-green-100 text-green-700 border-transparent text-[10px]">Al aangemaakt</Badge>}
                        </div>
                        <p className="text-sm font-medium text-black mt-0.5">{ctrl.name}</p>
                        {ctrl.description && <p className="text-[10px] text-neutral-500 mt-0.5 line-clamp-1">{ctrl.description}</p>}
                      </div>
                    </button>
                  );
                })}
            </div>
          </div>
        )}

        {fwStep === 2 && (
          <div className="space-y-3" data-testid="fw-step-confirm">
            <div className="p-3 bg-neutral-50 rounded-lg border border-neutral-200">
              <div className="flex items-center gap-2 mb-2"><Shield className="h-4 w-4 text-blue-600" strokeWidth={1.5} /><span className="text-sm font-semibold text-black">{selectedFramework?.name}</span></div>
              <p className="text-xs text-neutral-500"><strong>{selectedControls.length}</strong> controls geselecteerd om als maatregelen aan te maken.</p>
            </div>
            <p className="text-xs text-neutral-500">De volgende maatregelen worden aangemaakt:</p>
            <div className="space-y-1 max-h-[250px] overflow-y-auto">
              {frameworkControls.filter(c => selectedControls.includes(c.id)).map(ctrl => (
                <div key={ctrl.id} className="flex items-center gap-2 py-1.5 px-2 rounded bg-white border border-neutral-100" data-testid={`fw-confirm-${ctrl.id}`}>
                  <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" strokeWidth={1.5} />
                  <span className="text-xs font-medium text-black">{ctrl.code}: {ctrl.name}</span>
                  <span className="text-[10px] text-neutral-400 ml-auto">{ctrl.category || 'process'}</span>
                </div>
              ))}
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700">Alle maatregelen worden aangemaakt met status "Gepland". U kunt ze daarna individueel aanpassen, toewijzen aan leveranciers, en koppelen aan risico's.</p>
            </div>
          </div>
        )}
      </div>

      <DialogFooter className="flex justify-between sm:justify-between">
        <Button variant="outline" onClick={() => { fwStep === 0 ? onOpenChange(false) : setFwStep(fwStep - 1); }} data-testid="fw-wizard-back">
          {fwStep === 0 ? 'Annuleren' : <><ChevronLeft className="h-4 w-4 mr-1" strokeWidth={1.5} />Vorige</>}
        </Button>
        {fwStep === 1 && (
          <Button className="bg-black text-white hover:bg-neutral-800" onClick={() => setFwStep(2)} disabled={selectedControls.length === 0} data-testid="fw-wizard-next">
            {selectedControls.length} controls bevestigen <ChevronRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
          </Button>
        )}
        {fwStep === 2 && (
          <Button className="bg-black text-white hover:bg-neutral-800" onClick={handleCreateFromControls} disabled={fwCreating} data-testid="fw-wizard-create">
            {fwCreating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" strokeWidth={1.5} />}
            {selectedControls.length} maatregel(en) aanmaken
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
